import { ChevronDown, Copy, MapPin } from "lucide-react";
import { toast } from "sonner";

import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { ScrollArea } from "@/components/ui/scroll-area";
import { cn } from "@/lib/utils";
import {
  formatAdminUserDobDisplay,
  formatAdminUserDisplayName,
  formatAdminUserSex,
  patientMetaField,
  primaryEmailDisplay,
  primaryPhoneDisplay,
} from "@/lib/admin-user-display";

function copyValueToClipboard(value, successMessage) {
  const text = String(value || "").trim();
  if (!text) return;
  if (!navigator.clipboard?.writeText) {
    toast.error("Copy is not available in this environment.");
    return;
  }
  void navigator.clipboard.writeText(text).then(
    () => toast.success(successMessage),
    () => toast.error("Could not copy"),
  );
}

function CopyValueButton({ value, ariaLabel, successMessage }) {
  const text = String(value || "").trim();
  if (!text || text === "—") return null;
  return (
    <button
      type="button"
      aria-label={ariaLabel}
      title={ariaLabel}
      className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded text-muted-foreground transition hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      onClick={() => copyValueToClipboard(text, successMessage)}
    >
      <Copy className="h-3 w-3" strokeWidth={2} aria-hidden />
    </button>
  );
}

export function MergeSubjectIntro() {
  return (
    <p className="text-xs text-muted-foreground">
      User attached to this thread (merge subject) — only phone below; full records on matching users.
    </p>
  );
}

/** Name + role line (e.g. patient · active) for admin / migration views. */
export function AdminUserHeader({ user, compact }) {
  const pm = user?.patientMeta && typeof user.patientMeta === "object" ? user.patientMeta : {};
  const displayName = formatAdminUserDisplayName(user);
  const sex = formatAdminUserSex(user);
  const title = String(patientMetaField(pm, "title", "Title") || "").trim();

  return (
    <div className="min-w-0">
      <p className={cn("font-semibold tracking-tight text-foreground", compact ? "text-base" : "text-lg")}>
        {displayName || "—"}
      </p>
      <p className="mt-0.5 text-xs text-muted-foreground">
        {[title, user?.userType, user?.status, sex].filter(Boolean).join(" · ") || "—"}
      </p>
    </div>
  );
}

/** Identity, contact, address, and alerts — everything below the header. */
function MigrationUserDetailsBody({ user, compact, mergeSubjectContactPhoneOnly }) {
  const pm = user?.patientMeta && typeof user.patientMeta === "object" ? user.patientMeta : {};
  const userId = String(user?.id || "").trim();
  const loginId = String(user?.loginId || "").trim();
  const dob = formatAdminUserDobDisplay(user);
  const nric = String(patientMetaField(pm, "nric", "NRIC") || "").trim();
  const nricType = String(patientMetaField(pm, "nricType", "nric_type", "NRICType") || "").trim();
  const nationality = String(patientMetaField(pm, "nationality", "Nationality") || "").trim();
  const marital = String(patientMetaField(pm, "maritalStatus", "marital_status", "MaritalStatus") || "").trim();
  const givenId = String(patientMetaField(pm, "givenID", "given_id", "GivenID") || "").trim();
  const address = String(patientMetaField(pm, "address", "Address") || "").trim();
  const postal = String(patientMetaField(pm, "postal", "postal_code", "Postal") || "").trim();
  const unit = String(patientMetaField(pm, "unitNo", "unit_no", "UnitNo") || "").trim();
  const alerts = String(patientMetaField(pm, "alerts", "Alerts") || "").trim();

  const phone = primaryPhoneDisplay(user?.contacts);
  const email = primaryEmailDisplay(user?.contacts);

  const nricLine = nric || nricType ? [nric, nricType].filter(Boolean).join(" · ") : "";
  const roles = Array.isArray(user?.roles) ? user.roles.map((r) => String(r || "").trim()).filter(Boolean) : [];

  const fieldClass = compact ? "text-[11px]" : "text-sm";
  const labelClass = "text-[10px] font-semibold uppercase tracking-wide text-muted-foreground";
  const subGrid = cn("grid gap-3", compact ? "sm:grid-cols-2" : "sm:grid-cols-2 lg:grid-cols-3");

  const identityFields = !mergeSubjectContactPhoneOnly
    ? [
        userId
          ? {
              key: "user-id",
              label: "User ID",
              valueClass: cn(fieldClass, "font-mono text-foreground"),
              copyable: true,
              copyAria: "Copy user ID",
              copySuccess: "User ID copied",
              value: userId,
            }
          : null,
        loginId
          ? {
              key: "login",
              label: "Login",
              valueClass: cn(fieldClass, "break-all text-foreground"),
              copyable: true,
              copyAria: "Copy login",
              copySuccess: "Login copied",
              value: loginId,
            }
          : null,
        dob && dob !== "—"
          ? { key: "dob", label: "Date of birth", valueClass: cn(fieldClass, "text-foreground"), value: dob }
          : null,
        nricLine
          ? { key: "nric", label: "NRIC / ID", valueClass: cn(fieldClass, "text-foreground"), value: nricLine }
          : null,
        givenId
          ? { key: "givenId", label: "Given ID", valueClass: cn(fieldClass, "text-foreground"), value: givenId }
          : null,
        nationality
          ? {
              key: "nationality",
              label: "Nationality",
              valueClass: cn(fieldClass, "text-foreground"),
              value: nationality,
            }
          : null,
        marital
          ? {
              key: "marital",
              label: "Marital status",
              valueClass: cn(fieldClass, "text-foreground"),
              value: marital,
            }
          : null,
        roles.length > 0
          ? {
              key: "roles",
              label: "Roles",
              render: () => (
                <dd className="flex flex-wrap gap-1">
                  {roles.map((role) => (
                    <span
                      key={role}
                      className="inline-flex items-center rounded-full border border-primary/30 bg-primary/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-primary"
                    >
                      {role}
                    </span>
                  ))}
                </dd>
              ),
            }
          : null,
      ].filter(Boolean)
    : [];

  const contactFields = [
    phone && phone !== "—"
      ? {
          key: "phone",
          label: "Phone",
          valueClass: cn(fieldClass, "tabular-nums text-foreground"),
          copyable: true,
          copyAria: "Copy phone",
          copySuccess: "Phone copied",
          value: phone,
        }
      : null,
    !mergeSubjectContactPhoneOnly && email && email !== "—"
      ? {
          key: "email",
          label: "Email",
          valueClass: cn(fieldClass, "break-all text-foreground"),
          copyable: true,
          copyAria: "Copy email",
          copySuccess: "Email copied",
          value: email,
        }
      : null,
  ].filter(Boolean);

  const renderField = (field) => (
    <div key={field.key}>
      <dt className={labelClass}>{field.label}</dt>
      {field.render ? (
        field.render()
      ) : (
        <dd className={cn("flex items-center gap-1.5", field.valueClass)}>
          <span className="min-w-0 flex-1">{field.value}</span>
          {field.copyable ? (
            <CopyValueButton
              value={field.value}
              ariaLabel={field.copyAria}
              successMessage={field.copySuccess}
            />
          ) : null}
        </dd>
      )}
    </div>
  );

  const hasAddress = !mergeSubjectContactPhoneOnly && (address || postal || unit);
  const showAlerts = !mergeSubjectContactPhoneOnly && Boolean(alerts);

  return (
    <div className="space-y-4 pt-1">
      {identityFields.length > 0 ? (
        <div>
          <p className={cn(labelClass, "mb-2")}>Identity & record</p>
          <dl className={subGrid}>{identityFields.map(renderField)}</dl>
        </div>
      ) : null}

      {contactFields.length > 0 ? (
        <div>
          <p className={cn(labelClass, "mb-2")}>Contact</p>
          <dl className={subGrid}>{contactFields.map(renderField)}</dl>
        </div>
      ) : null}

      {hasAddress ? (
        <div>
          <p className={cn(labelClass, "mb-2")}>Address</p>
          <div
            className={cn(
              fieldClass,
              "flex items-start gap-2 rounded-md border border-border/50 bg-muted/30 px-3 py-2 text-foreground",
            )}
          >
            <MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden />
            <span className="whitespace-pre-line">
              {[unit, address, postal].filter(Boolean).join("\n") || "—"}
            </span>
          </div>
        </div>
      ) : null}

      {showAlerts ? (
        <div>
          <p className={cn(labelClass, "mb-2")}>Alerts &amp; notes</p>
          <ScrollArea
            className={cn(fieldClass, "rounded-md border border-amber-500/25 bg-amber-500/5")}
            viewportClassName="max-h-28 px-3 py-2 text-foreground"
          >
            {alerts.replace(/&nbsp;/g, " ").replace(/<[^>]*>/g, "")}
          </ScrollArea>
        </div>
      ) : null}
    </div>
  );
}

/**
 * Full user summary for migration dialogs (non-collapsible).
 * @param {{ onlyPhone?: boolean, hideHeader?: boolean }} props — `hideHeader` lets the
 *   caller render its own richer hero (avatar + name + chips) and skip AdminUserHeader.
 */
export function MigrationUserDetails({ user, compact, onlyPhone, hideHeader = false }) {
  const mergeSubjectContactPhoneOnly = Boolean(onlyPhone);

  return (
    <div
      className={cn(
        "space-y-4",
        mergeSubjectContactPhoneOnly && "rounded-md border border-border/40 bg-muted/25 px-3 py-2.5",
      )}
    >
      {mergeSubjectContactPhoneOnly ? <MergeSubjectIntro /> : null}
      {!hideHeader ? <AdminUserHeader user={user} compact={compact} /> : null}
      <MigrationUserDetailsBody user={user} compact={compact} mergeSubjectContactPhoneOnly={mergeSubjectContactPhoneOnly} />
    </div>
  );
}

/**
 * Collapsible admin user card: header always visible; expand for full details.
 * @param {{ onlyPhone?: boolean }} props
 */
export function CollapsibleMigrationUserDetails({
  user,
  compact,
  onlyPhone,
  defaultOpen = false,
  className,
  /** Stops row-level click handlers (e.g. radio selection) when toggling expand. */
  stopTriggerEventPropagation = false,
}) {
  const mergeSubjectContactPhoneOnly = Boolean(onlyPhone);

  return (
    <div
      className={cn(
        "w-full",
        mergeSubjectContactPhoneOnly && "rounded-md border border-border/40 bg-muted/25 px-3 py-2.5",
        className,
      )}
    >
      <Collapsible defaultOpen={defaultOpen} className="w-full">
        <div className="flex w-full items-start gap-2">
          <CollapsibleTrigger asChild>
            <button
              type="button"
              className={cn(
                "group mt-0.5 flex size-7 shrink-0 items-center justify-center rounded-md text-muted-foreground",
                "hover:bg-muted/80 hover:text-foreground",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background",
              )}
              aria-label="Toggle user details"
              onClick={stopTriggerEventPropagation ? (e) => e.stopPropagation() : undefined}
            >
              <ChevronDown className="size-4 shrink-0 transition-transform duration-200 group-data-[state=open]:rotate-180" />
            </button>
          </CollapsibleTrigger>
          <div className="min-w-0 flex-1 space-y-2">
            <AdminUserHeader user={user} compact={compact} />
            <CollapsibleContent>
              <div className="space-y-4 pt-1">
                {mergeSubjectContactPhoneOnly ? <MergeSubjectIntro /> : null}
                <MigrationUserDetailsBody
                  user={user}
                  compact={compact}
                  mergeSubjectContactPhoneOnly={mergeSubjectContactPhoneOnly}
                />
              </div>
            </CollapsibleContent>
          </div>
        </div>
      </Collapsible>
    </div>
  );
}
